<template>
  <div class="flex flex-col">
    <div class="mb-2 text-gray-500">
      Configure a new AI agent with specific capabilities and permissions
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Basic Info" name="basic">
        <el-form
          ref="formRef"
          :model="form"
          :rules="formRules"
          label-width="120px"
        >
          <div class="grid grid-cols-2 gap-6">
            <div>
              <el-form-item label="Agent Id" prop="agent_id">
                <el-input
                  :disabled="Boolean(value?.agent_id)"
                  v-model="form.agent_id"
                  placeholder="Enter agent id"
                  clearable
                />
              </el-form-item>
              <el-form-item label="Agent Name" prop="name">
                <el-input
                  v-model="form.name"
                  placeholder="Enter agent name"
                  clearable
                />
              </el-form-item>
              <el-form-item label="Agent Type">
                <SmartSelect
                  v-model="form.config.type"
                  :options="enumToOptions(AIAgentType)"
                  clearable
                  placeholder="Type"
                  class="w-full"
                />
              </el-form-item>
              <el-form-item label="Description">
                <el-input
                  type="textarea"
                  v-model="form.description"
                  placeholder="Describe the agent's purpose and capabilities"
                  autosize
                />
              </el-form-item>
            </div>
            <div>
              <el-form-item label="Agent Avatar">
                <AvatarUploader v-model="form.avatar_url" />
                <div class="text-xs text-gray-400 mt-2 text-center">
                  Recommended: 200x200px<br />
                  Max size: 5MB<br />
                  Formats: JPG, PNG, GIF
                </div>
              </el-form-item>
            </div>
          </div>
          <el-form-item label="Prompt">
            <el-input
              type="textarea"
              v-model="form.prompt.content"
              placeholder="Enter the prompt for the AI agent"
              :autosize="{ minRows: 10, maxRows: 20 }"
            />
          </el-form-item>
          <div class="grid grid-cols-2 gap-4">
            <el-form-item label="AI Model">
              <SmartSelect
                v-model="form.base_model"
                :options="enumToOptions(AIAgentModel)"
                display-field="name"
                value-field="id"
                value-type="string"
                placeholder="Select AI model"
                clearable
                class="w-full"
              />
            </el-form-item>
            <el-form-item label="History Limit">
              <el-input-number
                v-model="form.conversation_history_limit"
                controls-position="right"
                :min="1"
                :max="1000"
                placeholder="30"
                class="w-full"
              />
            </el-form-item>
          </div>
          <el-form-item label="Functions">
            <el-input
              v-model="functionQueryParameters.display_name"
              placeholder="Filter functions..."
              clearable
              class="mb-2"
            />
            <div class="flex h-64 relative">
              <el-scrollbar class="absolute inset-0 scroll-bar">
                <SmartCheckboxGroup
                  ref="functionsRef"
                  v-model="functions"
                  display-field="display_name"
                  value-field="function_id"
                  value-type="Array<Object>"
                  :load-method="getAgentFunctions"
                  :query-parameters="functionQueryParameters"
                  :like-fields="['display_name']"
                  class="flex flex-col"
                >
                  <template #default="{ item }">
                    <el-checkbox :label="item.key">
                      <i class="fa fa-cog mr-1" />
                      {{ item.value }}
                      <el-badge :value="item.rawData.type" class="ml-2" />
                    </el-checkbox>
                  </template>
                </SmartCheckboxGroup>
              </el-scrollbar>
            </div>
          </el-form-item>
          <el-form-item label="Suggestions">
            <SmartTag
              v-model="form.suggested_messages"
              addTagText="+ Add Suggestion"
            />
          </el-form-item>
          <el-form-item label="Time Range">
            <SmartDatePicker
              :startDate.sync="form.prompt.param.timeRange.startTime"
              :endDate.sync="form.prompt.param.timeRange.endTime"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 100%"
              clearable
            />
          </el-form-item>
        </el-form>
      </el-tab-pane>
      <el-tab-pane label="Devices" name="devices">
        <div>
          <div class="flex flex-row items-center py-2 gap-2">
            <div class="font-medium">Select Devices to Access</div>
            <el-button
              size="mini"
              icon="el-icon-check"
              @click="selectAllDevices"
              >Select All</el-button
            >
            <el-button
              size="mini"
              icon="el-icon-close"
              @click="unselectAllDevices"
              >Unselect All</el-button
            >
          </div>
          <el-input
            v-model="deviceQueryParameters.name"
            placeholder="Filter devices..."
            clearable
            class="mb-2"
          />
          <div class="flex h-64 relative">
            <el-scrollbar class="absolute inset-0 scroll-bar">
              <SmartCheckboxGroup
                ref="deviceCheckboxGroupRef"
                v-model="assetList"
                display-field="name"
                value-field="assetId"
                value-type="Array<Object>"
                :load-method="getAllAssetVo"
                :query-parameters="deviceQueryParameters"
                :like-fields="['name']"
                class="flex flex-col"
              >
                <template #default="{ item }">
                  <el-checkbox :label="item.key">
                    <i class="el-icon-cpu mr-1" />
                    {{ item.value }}
                    <el-badge :value="item.rawData.type" class="ml-2" />
                  </el-checkbox>
                </template>
              </SmartCheckboxGroup>
            </el-scrollbar>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
    <span class="flex flex-row justify-end">
      <el-button @click="handleCancel">Cancel</el-button>
      <el-button :loading="uploading" type="primary" @click="handleConfirm">
        {{ value ? "Update Agent" : "Create Agent" }}
      </el-button>
    </span>
  </div>
</template>
<script>
import _ from "lodash";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import SmartTag from "@/components/SmartControls/SmartTag.vue";
import { addOrUpdateAgent, getAllAssetVo } from "@/api";
import SmartCheckboxGroup from "@/components/SmartControls/SmartCheckboxGroup.vue";
import { defaultAgentPrompt, agentRecords } from "./default-agent-prompt";
import AvatarUploader from "@/components/AvatarUploader.vue";
import SmartDatePicker from "@/components/SmartControls/SmartDatePicker.vue";
import { createWebsocketShadowInstance, getAgentFunctions } from "@/api";
import { enumToOptions, AIAgentType, AIAgentModel } from "@/enums";
import { inputRule } from "@/utils/rules";

export default {
  name: "EditAiAgentDialog",
  components: {
    SmartSelect,
    SmartCheckboxGroup,
    AvatarUploader,
    SmartTag,
    SmartDatePicker,
  },
  emits: ["input"],
  props: {
    /** item */
    value: {
      type: Object,
      default: null,
    },
    /**用于指定type */
    defaultAgentPrompt: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    "form.base_model"(newVal) {
      // 选中模型后自动合并 agentRecords 配置
      if (agentRecords && agentRecords[newVal]) {
        const defaultModelConfig = agentRecords[newVal];
        this.form.base_model = newVal;
        this.form.config = defaultModelConfig.config;
        //如果默认模型中的config中没有llm_config需要删除
        if (!defaultModelConfig.config.llm_config) {
          delete this.form.config.llm_config;
        }
      }
    },
    "form.functions"(newVal) {
      // 自动同步 functions 的 display_name 到 suggested_messages，移除未选中的
      if (Array.isArray(newVal)) {
        const displayNames = newVal.map((f) => f.display_name).filter(Boolean);

        // 保留用户自定义的 suggested_messages（不是 function display_name 的项）
        const prev = Array.isArray(this.form.suggested_messages)
          ? this.form.suggested_messages
          : [];
        const customMessages = prev.filter(
          (msg) =>
            !this.$refs.functionsRef.allList.some((opt) => opt.value === msg)
        );
        // 只保留当前 functions 的 display_name 和用户自定义项
        this.form.suggested_messages = Array.from(
          new Set([...customMessages, ...displayNames])
        );
      }
    },
  },
  computed: {
    AIAgentModel: () => AIAgentModel,
    AIAgentType: () => AIAgentType,
    inputRule: () => inputRule,
    formRules: () => ({
      agent_id: [
        { required: true, message: "Agent Id is required", trigger: "blur" },
      ],
      name: [
        { required: true, message: "Agent Name is required", trigger: "blur" },
      ],
    }),
    assetList: {
      get() {
        return this.form.prompt.param.assetList.filter(
          (item) =>
            !(Object.keys(item).length === 0 && item.constructor === Object)
        );
      },
      /**
       * 只保留模型的两个字段
       * @param val
       */
      set(val) {
        const list = val.map((item) => ({
          assetId: item.assetId,
          name: item.name,
        }));
        this.form.prompt.param.assetList = list;
      },
    },
    // 只做增删，不覆盖已有 function 个性化设置
    functions: {
      get() {
        return this.form.functions;
      },
      set(newList) {
        const oldList = Array.isArray(this.form.functions)
          ? this.form.functions
          : [];
        const newIds = newList.map((f) => f.function_id);
        const merged = [];
        for (const oldFn of oldList) {
          if (newIds.includes(oldFn.function_id)) {
            merged.push(oldFn);
          }
        }
        for (const newFn of newList) {
          if (!oldList.some((f) => f.function_id === newFn.function_id)) {
            merged.push(newFn);
          }
        }
        this.form.functions = merged;
      },
    },
  },
  data() {
    // 组装优先级：默认模型 < 外部传入模型 < 用户配置模型
    // defaultAgentPrompt: props传入或默认
    const me = this;
    const merged = _.merge(
      {},
      defaultAgentPrompt,
      me.value,
      me.defaultAgentPrompt
    );
    const form = merged;

    // 补充部分默认字段（如 agent_id、avatar_url、base_model 等）
    if (!form.agent_id) {
      form.agent_id = `test-${Date.now()}`;
    }
    if (!form.base_model) {
      form.base_model = "gpt-4o";
    }
    if (!form.name) {
      form.name = "A Test Agent";
    }
    if (!form.avatar_url) {
      form.avatar_url =
        "https://storage.googleapis.com/my-agents.squarecooky.com/avatars/presales-agent-01.png";
    }
    if (!form.conversation_history_limit) {
      form.conversation_history_limit = 100;
    }
    if (!form.config) {
      form.config = { type: "support" };
    }
    // 保证suggested_messages为数组
    if (!form.suggested_messages || !Array.isArray(form.suggested_messages)) {
      form.suggested_messages = defaultAgentPrompt.suggested_messages || [];
    }
    return {
      deviceQueryParameters: {
        name: "",
      },
      functionQueryParameters: {
        display_name: "",
      },
      uploading: false,
      form,
      activeTab: "basic",
    };
  },
  methods: {
    getAgentFunctions,
    getAllAssetVo,
    enumToOptions,
    handleAvatarChange() {},
    beforeAvatarUpload() {
      return true;
    },
    handleCancel() {
      this.$emit("cancel");
    },
    async createNewWebsocket(agentId) {
      // 创建 websocket
      const { id } = await createWebsocketShadowInstance(agentId);
      return id;
    },
    async handleConfirm() {
      const me = this;
      try {
        await this.$refs.formRef.validate();
      } catch (err) {
        me.activeTab = "basic";
        me.$message.error("Please fill in the required fields");
        return;
      }
      const userId = this.$store.state.user.id;
      me.form.prompt.param["user_id"] = userId;
      //如果是创建需要新创建一个websocket连接try {
      try {
        me.uploading = true;
        if (this.value == undefined) {
          me.form.websocket = await me.createNewWebsocket(me.form.agent_id);
        }
        await addOrUpdateAgent(me.form);
        console.log(
          "Submitted form:",
          JSON.stringify(me.form.prompt.param.assetList)
        );
      } catch (error) {
        me.$message.error(
          `Failed to ${me.value ? "update" : "create"} agent: ${
            error.message || error
          }`
        );
        return;
      } finally {
        me.uploading = false;
      }
      me.$emit("confirm", me.form);
    },
    selectAllDevices() {
      this.$refs.deviceCheckboxGroupRef.selectAll();
    },
    unselectAllDevices() {
      this.$refs.deviceCheckboxGroupRef.unselectAll();
    },
  },
};
</script>
